import { observer } from "mobx-react-lite";
import { useCallback } from "react";

import { useStructuredSearchStore } from "../stores";
import FilterBuilderRow from "../FilterBuilderRow";

import EditPanelActions from "./EditPanelActions";

import { cn } from "@/lib/utils";

interface FilterEditPanelProps {
  editInputRef: React.RefObject<HTMLInputElement | null>;
  onStructuredKeyDown: (event: React.KeyboardEvent) => void;
  variant?: "default" | "compact";
}

const FilterEditPanel = observer(
  ({ editInputRef, onStructuredKeyDown, variant = "default" }: FilterEditPanelProps) => {
    const store = useStructuredSearchStore();
    const isCompact = variant === "compact";

    const handleSave = useCallback(() => {
      store.completeFilter();
    }, [store]);

    const handleCancel = useCallback(() => {
      store.cancelEdit();
    }, [store]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.defaultPrevented) {
          return;
        }

        if (e.key === "Enter") {
          if (store.isMultiSelectMode && store.step === "value" && !e.metaKey) {
            return;
          }
          e.preventDefault();
          handleSave();
        } else if (e.key === "Escape") {
          e.preventDefault();
          handleCancel();
        }
      },
      [handleSave, handleCancel, store.isMultiSelectMode, store.step],
    );

    return (
      <div
        className={cn(
          "relative border border-t-0 border-gray-200/80 bg-[#f7f7f8] shadow-md shadow-gray-950/10 transition-all duration-200 ease-out",
          isCompact ? "rounded-b-xl" : "rounded-b-2xl",
          store.editingFilterId
            ? cn(
                "z-[100] max-h-32 translate-y-0 overflow-visible bg-white opacity-100",
                isCompact ? "px-3 pb-2" : "px-4 pb-3",
              )
            : cn(
                "pointer-events-none max-h-0 -translate-y-2 overflow-hidden py-0 opacity-0",
                isCompact ? "px-3" : "px-4",
              ),
        )}
        onKeyDown={handleKeyDown}
        aria-hidden={!store.editingFilterId}
      >
        {store.editingFilterId && (
          <>
            <div className="mb-1 flex items-start justify-between gap-1">
              <span
                className={cn(
                  "pointer-events-none font-semibold uppercase tracking-[0.08em] text-gray-400",
                  isCompact ? "text-[10px]" : "text-[11px]",
                )}
              >
                Edit
              </span>
            </div>
            <div className="flex min-w-0 items-start gap-2">
              <FilterBuilderRow
                inputRef={editInputRef}
                placeholder="Enter value, press Enter to add"
                isEditMode
                showAutocompleteDropdown={false}
                onStructuredKeyDown={onStructuredKeyDown}
              />
              <EditPanelActions onSave={handleSave} onCancel={handleCancel} />
            </div>
          </>
        )}
      </div>
    );
  },
);

export default FilterEditPanel;
